'use client'

import { useRef, useEffect, useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSession } from 'next-auth/react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { apiClient } from '@/lib/api-client'
import { useAI, type ChatMessage as ChatMessageType, type ToolCall } from '@/context/AIContext'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { pendingActionOf } from './ActionCard'
import { CapabilityBubbles, contextChipParts, type PageContextInput } from './CapabilityBubbles'

// Mutating action tools: when the AI executes one of these, broadcast a
// refresh so the open AR page reflects the change (see useArDataChanged).
const MUTATING_TOOLS = new Set<string>([
  'approve_deduction',
  'reject_deduction',
  'update_invoice_status',
  'apply_payment_to_invoice',
  'create_promise_to_pay',
  'record_dunning_action',
  'adjust_credit_limit',
  'create_ai_policy',
])

// Client timeout, above the server's 40 s answer budget (plan 12 section 5.2).
const CHAT_TIMEOUT_MS = 45_000

// User-facing messages (plan 12 section 8.3).
const MSG_TIMEOUT =
  'That took longer than expected, so I stopped. Your question is still in the box below. Try again, or ask something narrower.'
const MSG_ERROR = 'I could not complete that request. Nothing was changed. Try again in a moment.'
const MSG_ACTION_FAILED = 'The action could not be completed. Nothing was changed.'

type ToolResult = Record<string, unknown> | undefined

/** A real, successful execution: not a confirm-first preview, not a failure. */
function isExecuted(tc: { name: string; result?: unknown }): boolean {
  if (!MUTATING_TOOLS.has(tc.name)) return false
  const r = tc.result as ToolResult
  return !(r && typeof r === 'object' && (r.requires_confirmation || r.error))
}

/** Tell any open AR page to refetch after an executed action (see useArDataChanged). */
function broadcastExecuted(calls: { name: string; result?: unknown }[]) {
  const executed = calls.filter(isExecuted)
  if (executed.length === 0) return
  window.dispatchEvent(
    new CustomEvent('ar-data-changed', {
      detail: {
        tools: executed.map((tc) => tc.name),
        results: executed.map((tc) => tc.result),
      },
    })
  )
}

/** Tool arguments as previewed, without the confirmed flag. */
function previewArgs(tc: ToolCall): Record<string, unknown> {
  const args = { ...(tc.args ?? {}) }
  delete args.confirmed
  return args
}

/** Page context from the URL: Explorer bucket, Workbench tab, policy highlight, open ticket. */
function readUrlContext(pathname: string): Record<string, string> {
  const out: Record<string, string> = {}
  if (typeof window === 'undefined') return out
  const qs = new URLSearchParams(window.location.search)
  const queue = qs.get('queue') || (pathname.startsWith('/receivables') ? qs.get('status') || qs.get('tab') : null)
  if (queue) out.queue = queue
  if (pathname.startsWith('/workbench') && qs.get('tab')) out.tab = qs.get('tab') as string
  if (qs.get('highlight')) out.highlight = qs.get('highlight') as string
  const ticket = pathname.match(/^\/queries\/([0-9a-fA-F-]{36})/)
  if (ticket) out.ticket_id = ticket[1]
  const invoiceParam = qs.get('invoice') || qs.get('invoice_id')
  if (invoiceParam && pathname.startsWith('/workbench')) out.invoice_id = invoiceParam
  return out
}

// ============================================================================
// Animation Variants
// ============================================================================

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

const modalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300,
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.15,
    }
  },
}

// ============================================================================
// AI Manager Component
// ============================================================================

export function AIManager() {
  const { data: session } = useSession()
  const {
    isManagerOpen,
    closeManager,
    chatHistory,
    addMessage,
    updateMessage,
    clearHistory,
    isTyping,
    setIsTyping,
    currentPageContext,
    selectedData,
    pendingSeedMessage,
    consumeSeedMessage,
  } = useAI()

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [restore, setRestore] = useState<{ text: string; nonce: number } | null>(null)
  const [urlContext, setUrlContext] = useState<Record<string, string>>({})

  // Re-read the URL context whenever the panel opens or the page changes.
  useEffect(() => {
    if (isManagerOpen) setUrlContext(readUrlContext(currentPageContext))
  }, [isManagerOpen, currentPageContext])

  // Abort an in-flight request when the panel unmounts.
  useEffect(() => () => abortRef.current?.abort(), [])

  const sd = (selectedData ?? {}) as Record<string, unknown>
  const pageCtx: PageContextInput = useMemo(() => ({
    page: currentPageContext,
    invoiceNumber: typeof sd.invoice_number === 'string' ? sd.invoice_number : null,
    queue: urlContext.queue ?? (typeof sd.queue === 'string' ? sd.queue : null),
    ticketNumber: typeof sd.ticket_number === 'string' ? sd.ticket_number : null,
    highlight: urlContext.highlight ?? null,
    allowedActions: Array.isArray(sd.allowed_actions) ? (sd.allowed_actions as string[]) : null,
  }), [currentPageContext, sd.invoice_number, sd.queue, sd.ticket_number, sd.allowed_actions, urlContext])
  const chipParts = contextChipParts(pageCtx)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [chatHistory, isTyping])

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isManagerOpen) {
        closeManager()
      }
    }
    
    if (isManagerOpen) {
      document.addEventListener('keydown', handleKeyDown)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isManagerOpen, closeManager])

  // Focus trap - focus the modal when it opens
  useEffect(() => {
    if (isManagerOpen && modalRef.current) {
      modalRef.current.focus()
    }
  }, [isManagerOpen])

  // Handle sending a message
  const handleSendMessage = useCallback(async (content: string) => {
    // The last answer's unconfirmed preview, so a typed "yes" runs it directly.
    const lastAnswer = [...chatHistory].reverse().find(m => m.role === 'assistant' && !m.isLoading)
    const pendingCall = lastAnswer && !lastAnswer.action ? pendingActionOf(lastAnswer) : null
    const pending_action = pendingCall ? { tool: pendingCall.name, args: previewArgs(pendingCall) } : undefined

    // Add user message
    addMessage({ role: 'user', content })
    setSuggestions([])

    // Add loading message
    addMessage({ role: 'assistant', content: '', isLoading: true })
    setIsTyping(true)

    try {
      // Call backend API using apiClient
      interface ToolCallResponse {
        id: string
        name: string
        args: Record<string, unknown>
        result?: unknown
      }
      interface DownloadResponse {
        label: string
        url: string
        kind?: 'report' | 'invoice' | 'sales_order' | 'document'
      }
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      const timer = setTimeout(() => controller.abort(), CHAT_TIMEOUT_MS)
      const data = await apiClient.post<{
        response: string
        tool_calls?: ToolCallResponse[]
        downloads?: DownloadResponse[]
        suggestions?: string[]
      }>('/api/ai/chat', {
        message: content,
        history: chatHistory.filter(m => !m.isLoading).map(m => ({
          role: m.role,
          content: m.content,
        })),
        context: {
          page: currentPageContext,
          // Open-entity grounding: the invoice on screen and its document ids,
          // plus the page context from the URL (bucket, tab, highlight, ticket).
          selected_data: { ...readUrlContext(currentPageContext), ...(selectedData ?? {}) },
        },
        ...(pending_action ? { pending_action } : {}),
      }, { signal: controller.signal }).finally(() => clearTimeout(timer))

      // Remove loading message and add real response
      addMessage({
        role: 'assistant',
        content: data.response || MSG_ERROR,
        toolCalls: data.tool_calls,
        downloads: data.downloads,
      })

      // A typed "yes" ran the pending preview: settle its action card.
      if (pendingCall && lastAnswer) {
        const ran = (data.tool_calls || []).find(
          (tc) => tc.name === pendingCall.name && tc.args?.confirmed === true
        )
        if (ran) {
          const r = ran.result as ToolResult
          const failed = !r || typeof r !== 'object' || Boolean(r.error || r.requires_confirmation)
          updateMessage(lastAnswer.id, {
            action: { status: failed ? 'failed' : 'confirmed', message: data.response || undefined },
          })
        }
      }

      // If the AI actually executed a mutating action (not just a confirm-first
      // preview), broadcast so any open AR page refetches and shows the change.
      setSuggestions(Array.isArray(data.suggestions) ? data.suggestions.slice(0, 3) : [])
      broadcastExecuted(data.tool_calls || [])
    } catch (err) {
      const aborted =
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && /abort/i.test(err.name + ' ' + err.message))
      addMessage({ role: 'assistant', content: aborted ? MSG_TIMEOUT : MSG_ERROR })
      if (aborted) setRestore({ text: content, nonce: Date.now() })
    } finally {
      setIsTyping(false)
    }
  }, [addMessage, updateMessage, chatHistory, currentPageContext, selectedData, setIsTyping])

  // Confirm button on an action card: run the exact previewed tool call.
  const handleConfirmAction = useCallback(async (message: ChatMessageType, tc: ToolCall) => {
    if (message.action?.status === 'running') return
    updateMessage(message.id, { action: { status: 'running' } })
    try {
      const data = await apiClient.post<{ ok: boolean; message: string; result?: unknown }>(
        '/api/ai/actions/confirm',
        { tool: tc.name, args: previewArgs(tc) }
      )
      if (data.ok) {
        updateMessage(message.id, { action: { status: 'confirmed', message: data.message } })
        addMessage({ role: 'assistant', content: data.message })
        broadcastExecuted([{ name: tc.name, result: data.result }])
      } else {
        updateMessage(message.id, { action: { status: 'failed', message: data.message || MSG_ACTION_FAILED } })
      }
    } catch {
      updateMessage(message.id, { action: { status: 'failed', message: MSG_ACTION_FAILED } })
    }
  }, [addMessage, updateMessage])

  const handleCancelAction = useCallback((message: ChatMessageType) => {
    updateMessage(message.id, { action: { status: 'cancelled' } })
  }, [updateMessage])

  // Auto-send a one-shot starter message when opened from an entity surface
  // (e.g. the "Ask the AI Assistant" affordance on the invoice drawer).
  useEffect(() => {
    if (!isManagerOpen) return
    if (!pendingSeedMessage) return
    const seed = consumeSeedMessage()
    if (seed) handleSendMessage(seed)
    // Intentionally run only when the manager opens / a seed is queued.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManagerOpen, pendingSeedMessage])

  // Handle quick action click
  const handleQuickAction = (action: string) => {
    handleSendMessage(action)
  }

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeManager()
    }
  }

  // Only render in browser (for portal)
  if (typeof window === 'undefined') return null

  return createPortal(
    <AnimatePresence mode="wait">
      {isManagerOpen && (
        <>
          {/* Backdrop with blur - covers entire viewport including sidebar */}
          <motion.div
            key="ai-manager-backdrop"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2 }}
            onClick={handleBackdropClick}
            className="fixed inset-0 z-modal-backdrop flex items-center justify-center p-4 sm:p-8 md:p-12"
            style={{
              backgroundColor: 'rgba(26, 35, 64, 0.5)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
            aria-hidden="true"
          >
            {/* Modal Container - centered and properly sized */}
            <motion.div
              ref={modalRef}
              key="ai-manager-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ai-manager-title"
              aria-describedby="ai-manager-description"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                // Z-index for proper stacking
                'z-modal',
                // Size and positioning
                'relative w-full max-w-2xl',
                'h-[80vh] max-h-[700px] min-h-[450px]',
                // Solid white background for better readability
                'bg-white',
                // Border and shadow
                'border border-gray-200',
                'rounded-2xl',
                'shadow-2xl',
                // Layout
                'flex flex-col',
                'overflow-hidden'
              )}
            >
              {/* Decorative gradient border */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-cornflower/20 via-transparent to-brand-purple/20 pointer-events-none" />
              
              {/* Header */}
              <div className="relative border-b border-border/30 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* AI Icon */}
                    <div className={cn(
                      'flex h-12 w-12 items-center justify-center rounded-xl',
                      'bg-gradient-to-br from-brand-navy via-brand-purple to-brand-cornflower',
                      'shadow-lg shadow-brand-purple/25',
                      'ring-2 ring-white/50'
                    )}>
                      <Icons.sparkles className="h-6 w-6 text-white" strokeWidth={1.5} />
                    </div>
                    
                    <div>
                      <h2 
                        id="ai-manager-title"
                        className="text-xl font-semibold text-brand-navy"
                      >
                        AI Manager
                      </h2>
                      <p 
                        id="ai-manager-description"
                        className="text-sm text-muted-foreground"
                      >
                        Your intelligent command center assistant
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {chatHistory.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearHistory}
                        className="text-muted-foreground hover:text-foreground gap-1.5"
                        title="Clear conversation"
                      >
                        <Icons.trash className="h-4 w-4" strokeWidth={1.5} />
                        <span className="hidden sm:inline">Clear</span>
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={closeManager}
                      className="text-muted-foreground hover:text-foreground hover:bg-red-50 hover:text-red-600"
                      title="Close (Esc)"
                      aria-label="Close AI Manager"
                    >
                      <Icons.close className="h-5 w-5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>

                {/* Page Context Badge */}
                <div className="mt-4 flex items-center gap-3">
                  <div className={cn(
                    'inline-flex items-center gap-2 rounded-full px-3 py-1.5',
                    'bg-gradient-to-r from-brand-cornflower/10 to-brand-purple/10',
                    'text-brand-navy border border-brand-cornflower/20',
                    'text-sm font-medium'
                  )}>
                    <Icons.globe className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
                    <span className="truncate max-w-[22rem]">Context: {chipParts.join(' \u00b7 ')}</span>
                  </div>
                  
                  <kbd className={cn(
                    'hidden items-center rounded-md px-2 py-1 sm:inline-flex',
                    'text-xs font-medium',
                    'border border-border/50 bg-muted/50 text-muted-foreground'
                  )}>
                    <Icons.command className="h-3 w-3 mr-1" />
                    J to toggle
                  </kbd>
                </div>
              </div>

              {/* Messages Area */}
              <div className="relative flex-1 overflow-y-auto px-6 py-5">
                {chatHistory.length === 0 ? (
                  /* Empty State */
                  <div className="flex h-full flex-col items-center justify-center text-center py-8">
                    <div className={cn(
                      'mb-6 flex h-20 w-20 items-center justify-center rounded-2xl',
                      'bg-gradient-to-br from-brand-cornflower/20 to-brand-purple/20',
                      'ring-1 ring-brand-cornflower/30'
                    )}>
                      <Icons.messageSquare className="h-10 w-10 text-brand-cornflower" strokeWidth={1.5} />
                    </div>
                    
                    <h3 className="font-display text-xl font-semibold text-brand-navy">
                      How can I help you?
                    </h3>
                    <p className="mt-2 max-w-sm text-muted-foreground">
                      Ask me anything about your data, or use a quick action below.
                    </p>

                    {/* Quick Actions - Capability Bubbles */}
                    <div className="mt-8 w-full max-w-lg">
                      <CapabilityBubbles onSelect={handleQuickAction} context={pageCtx} />
                    </div>
                  </div>
                ) : (
                  /* Chat Messages */
                  <div className="space-y-4">
                    {chatHistory.map((message) => (
                      <ChatMessage
                        key={message.id}
                        message={message}
                        userName={session?.user?.name || undefined}
                        userImage={session?.user?.image}
                        onConfirmAction={handleConfirmAction}
                        onCancelAction={handleCancelAction}
                      />
                    ))}
                    {!isTyping && suggestions.length > 0 && (
                      <div className="pl-11">
                        <CapabilityBubbles onSelect={handleQuickAction} prompts={suggestions} compact />
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="relative border-t border-border/30">
                <ChatInput
                  onSend={handleSendMessage}
                  disabled={isTyping}
                  placeholder={isTyping ? 'AI is thinking...' : 'Ask anything...'}
                  restore={restore}
                />
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}
